import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  SyncHealthApi,
  SyncWatchAlertDto,
  SyncWatchAlertKind,
  SyncWatchDeviceDto,
  SyncWatchOverviewDto,
  SyncWatchSetAsideDeviceDto,
  SyncWatchState,
  SyncWatchTripDto,
} from '@buspro/web/apis';
import { EmptyStateComponent, ResourcesLoadingSkeletonsComponent } from '@buspro/web/shared';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { lastValueFrom } from 'rxjs';

/** How each POS state reads, and how it looks. Full class strings so the tints survive. */
const STATE_VIEW: Record<SyncWatchState, { label: string; badge: string; hint: string }> = {
  [SyncWatchState.Stuck]: {
    label: 'Not uploading',
    badge: 'badge badge-error',
    hint: 'The POS is online, but its changes have been waiting too long. Call the conductor.',
  },
  [SyncWatchState.Silent]: {
    label: 'Silent',
    badge: 'badge badge-warning',
    hint: 'Nothing from the POS for too long: no signal, switched off, or the app is closed.',
  },
  [SyncWatchState.Waiting]: {
    label: 'Sending',
    badge: 'badge badge-info',
    hint: 'Changes are waiting, but not for long — a normal drain.',
  },
  [SyncWatchState.Ok]: {
    label: 'Up to date',
    badge: 'badge badge-success',
    hint: 'Heard from recently, nothing waiting.',
  },
  [SyncWatchState.NoHeartbeat]: {
    label: 'Not reporting',
    badge: 'badge badge-ghost',
    hint: 'This POS runs an app older than the sync watch (before 2.25.0), so it cannot be judged.',
  },
};

/** Worst first: the rows someone has to act on are at the top. */
const STATE_ORDER: SyncWatchState[] = [
  SyncWatchState.Stuck,
  SyncWatchState.Silent,
  SyncWatchState.Waiting,
  SyncWatchState.NoHeartbeat,
  SyncWatchState.Ok,
];

/**
 * The sync watch: every trip on the road and whether its POS is getting its
 * changes to the server. Platform staff only.
 *
 * Born from 2026-09-17, when a POS stopped uploading for two hours while
 * online and nobody knew until the bus reached its destination. The server
 * checks every two minutes and pushes an alert when a POS is silent or stuck
 * for twenty; this page is where that alert leads.
 */
@Component({
  selector: 'buspro-admin-sync-watch-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzSpinModule,
    NzTableModule,
    NzToolTipModule,
    EmptyStateComponent,
    ResourcesLoadingSkeletonsComponent,
  ],
  templateUrl: './sync-watch-page.component.html',
})
export class SyncWatchPageComponent {
  private readonly syncHealthApi = inject(SyncHealthApi);

  /** '' is "all tenants". */
  readonly companyId = signal('');

  readonly watchQuery = injectQuery(() => ({
    queryKey: ['sync-watch', this.companyId()],
    queryFn: () =>
      lastValueFrom(this.syncHealthApi.syncHealthControllerWatch({ companyId: this.companyId() || undefined })),
    // The server looks every two minutes; half a minute keeps the page current without load.
    refetchInterval: 30_000,
    placeholderData: (previousData: SyncWatchOverviewDto | undefined) => previousData,
  }));

  readonly isInitialLoading = computed(() => this.watchQuery.isPending());
  readonly isRefreshing = computed(() => this.watchQuery.isFetching() && !this.isInitialLoading());

  readonly overview = computed(() => this.watchQuery.data() ?? null);

  readonly trips = computed(() =>
    [...(this.overview()?.trips ?? [])].sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state)),
  );
  readonly openAlerts = computed(() => this.overview()?.openAlerts ?? []);
  readonly recentAlerts = computed(() => this.overview()?.recentAlerts ?? []);
  readonly tenants = computed(() => this.overview()?.tenants ?? []);
  readonly setAsideDevices = computed(() => this.overview()?.setAsideDevices ?? []);

  /** Counts per state, or null while unknown (shown as "–", never 0). */
  readonly counts = computed(() => {
    const o = this.overview();
    if (!o) return null;
    const count = (s: SyncWatchState) => o.trips.filter((t) => t.state === s).length;
    return {
      onTheRoad: o.trips.length,
      stuck: count(SyncWatchState.Stuck),
      silent: count(SyncWatchState.Silent),
      notReporting: count(SyncWatchState.NoHeartbeat),
    };
  });

  /** How a POS state reads. A method, not the map: table rows reach the template untyped. */
  view(state: SyncWatchState): { label: string; badge: string; hint: string } {
    return STATE_VIEW[state] ?? STATE_VIEW[SyncWatchState.NoHeartbeat];
  }
  readonly AlertKind = SyncWatchAlertKind;

  selectTenant(id: string): void {
    this.companyId.set(id ?? '');
  }

  refresh(): void {
    void this.watchQuery.refetch();
  }

  /** "12 min", "1 h 05 min"; "–" when unknown. */
  duration(ms: number | null | undefined): string {
    if (ms === null || ms === undefined) return '–';
    const minutes = Math.max(0, Math.floor(ms / 60_000));
    if (minutes < 1) return 'under a minute';
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    return `${h} h ${String(minutes % 60).padStart(2, '0')} min`;
  }

  /** Time between two instants, for how long an alert lasted. */
  lasted(from: string, to: string | null | undefined): string {
    if (!to) return '–';
    return this.duration(new Date(to).getTime() - new Date(from).getTime());
  }

  waiting(device: SyncWatchDeviceDto | null | undefined): string {
    if (!device) return '–';
    if (device.pending === 0) return 'Nothing';
    const changes = device.pending === 1 ? '1 change' : `${device.pending} changes`;
    return `${changes}, ${this.duration(device.pendingAgeMs)}`;
  }

  /**
   * The part of that wait the phone was actually online, which is what the
   * alert judges. A dead zone is not a stall. Blank on a POS too old to
   * report it, and when the two are the same.
   */
  waitingOnline(device: SyncWatchDeviceDto | null | undefined): string {
    if (!device || device.pending === 0 || device.pendingOnlineMs == null) return '';
    if (device.pendingAgeMs != null && device.pendingAgeMs - device.pendingOnlineMs < 60_000) return '';
    return `${this.duration(device.pendingOnlineMs)} of it online`;
  }

  alertLabel(alert: SyncWatchAlertDto): string {
    return alert.kind === SyncWatchAlertKind.Stuck ? 'Not uploading' : 'Silent';
  }

  alertBadge(alert: SyncWatchAlertDto): string {
    return alert.kind === SyncWatchAlertKind.Stuck ? 'badge badge-error' : 'badge badge-warning';
  }

  resolutionLabel(resolution: string | null | undefined): string {
    switch (resolution) {
      case 'recovered':
        return 'Recovered';
      case 'tripEnded':
        return 'Trip ended';
      case 'notWatched':
        return 'Device gone quiet for a day';
      default:
        return resolution ?? '–';
    }
  }

  telHref(phone: string): string {
    return `tel:${phone.replace(/\s+/g, '')}`;
  }

  trackTrip(_: number, trip: SyncWatchTripDto): string {
    return trip.operationId;
  }

  trackAlert(_: number, alert: SyncWatchAlertDto): string {
    return alert.id;
  }

  trackDevice(_: number, device: SyncWatchSetAsideDeviceDto): string {
    return device.clientId;
  }
}
